import PropTypes from 'prop-types';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { useTheme } from '@mui/material/styles';
import {
  Box,
  Grid,
  Card,
  List,
  Stack,
  Backdrop,
  Container,
  Typography,
} from '@mui/material';

import { useToast } from 'src/hooks/useToast';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';

import Loader from 'src/components/loaders/Loader';
import ActionBar from 'src/components/charts/ActionBar';

import NoSubmissions from './NoSubmissions';
import { getOSName, parseDuration } from './utils';
import DateInpuCard from './questionCards/DateInputCard';
import TextInputCard from './questionCards/TextInputCard';
import FileInputCard from './questionCards/FileInputCard';
import MultipleInputCard from './questionCards/MultipleInputCard';
import { getTicketsByFormLink } from '../../../services/ticket.service';
import AccordionSection from '../../../components/accordion/AccordionSection';
import { fetchFromLinkVisitsNbrById } from '../../../services/form.link.service';
import {
  PieChart,
  BarChart,
  LineChart,
  AppWidgetSummary,
} from '../../../components/charts';

Surveydashboard.propTypes = {
  selectedFormId: PropTypes.any,
};

export default function Surveydashboard({ selectedFormId }) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const [shareableformData, setShareableformData] = useState(null);
  const [ticketsByForm, setTicketsByForm] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const { showToast, hideToast } = useToast();

  const cardStyle = {
    maxWidth: { xs: '100%', sm: 260 },
    minHeight: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 3,
    py: 0,
    gap: 1,
    textAlign: 'left',
  };

  const getSharableFormById = async () => {
    genericApiToaster(
      fetchFromLinkVisitsNbrById,
      [selectedFormId.id],
      '',
      t('errorMessages.couldntLoadDep'),
      showToast,
      hideToast,
      t,
      (res) => {
        setShareableformData(res);
      }
    );
  };

  const getTicketsByFormId = async () => {
    let ticksByForm = null;
    await genericApiToaster(
      getTicketsByFormLink,
      [selectedFormId.id],
      '',
      t('errorMessages.couldntLoadDep'),
      showToast,
      hideToast,
      t,
      (res) => {
        setTicketsByForm(res);
        ticksByForm = res;
      }
    );
    return ticksByForm;
  };

  const loadData = useCallback(async () => {
    setIsLoading(true);
    if (selectedFormId) {
      getSharableFormById();
      const ticksByForm = await getTicketsByFormId();
      if (ticksByForm) {
        const mappedSections = mapSectionsQuestionsResponses(ticksByForm);
        setSections(mappedSections);
        setTicketsByForm(ticksByForm);
      }
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormId]);

  const questionHeaders = useMemo(() => {
    if (!Array.isArray(ticketsByForm)) return [];

    const headers = ticketsByForm.flatMap(
      (submission) =>
        submission.data?.flatMap(
          (section) =>
            section.questions?.map(
              (question, index) =>
                `Section ${section.sectionName} : ${question.text} (Q${index + 1})`
            ) || []
        ) || []
    );

    return Array.from(new Set(headers));
  }, [ticketsByForm]);

  const metadataLabels = useMemo(
    () => ({
      submitter: t('surveyDashboard.metadataHeaders.submitter'),
      submittedAt: t('surveyDashboard.metadataHeaders.submittedAt'),
      device: t('surveyDashboard.metadataHeaders.device'),
      os: t('surveyDashboard.metadataHeaders.os'),
      browser: t('surveyDashboard.metadataHeaders.browser'),
      fillingDuration: t('surveyDashboard.metadataHeaders.duration'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );

  useEffect(() => {
    if (ticketsByForm?.length > 0 && questionHeaders.length > 0) {
      const csv = surveyDataToCSV(ticketsByForm, metadataLabels, questionHeaders);
      setCsvData(csv);
    }
  }, [ticketsByForm, questionHeaders, metadataLabels, shareableformData]);

  function surveyDataToCSV(submissions, metadataLabels, questionHeaders = []) {
    if (!Array.isArray(submissions) || submissions.length === 0) return '';

    const submitterLabel = metadataLabels.submitter;
    const submittedAtLabel = metadataLabels.submittedAt;
    const deviceLabel = metadataLabels.device;
    const osLabel = metadataLabels.os;
    const browserLabel = metadataLabels.browser;
    const fillingdurationLabel = metadataLabels.fillingDuration;

    const metadataHeaders = [
      submitterLabel,
      submittedAtLabel,
      deviceLabel,
      osLabel,
      browserLabel,
      fillingdurationLabel,
    ];

    const allHeaders = [...metadataHeaders, ...questionHeaders];

    const csvRows = submissions.map((submission) => {
      const row = {
        [submitterLabel]: submission.createdByEmail || '',
        [submittedAtLabel]:
          format(parseISO(submission.createdAt), 'M/d/yyyy, h:mm:ss a') || '',
        [deviceLabel]: submission.device || '',
        [osLabel]: getOSName(submission.os) || '',
        [browserLabel]: submission.browser || '',
        [fillingdurationLabel]: parseDuration(submission.formFillingDuration || '0s'),
      };

      const entries =
        submission.data?.flatMap(
          (section) =>
            section.questions?.map((question, index) => {
              const key = `Section ${section.sectionName} : ${question.text} (Q${
                index + 1
              })`;
              const value = Array.isArray(question.inputValue)
                ? question.inputValue.join(',')
                : question.inputValue ?? '';
              return [key, value];
            }) || []
        ) || [];

      entries.forEach(([key, value]) => {
        row[key] = value;
      });

      const rowValues = allHeaders.map((header) => {
        const val = row[header] || '';
        const escaped = String(val).replace(/"/g, '""');
        return `"${escaped}"`;
      });

      return rowValues;
    });

    return [allHeaders, ...csvRows].map((r) => r.join(',')).join('\n');
  }

  useEffect(() => {
    loadData();
  }, [loadData, selectedFormId]);

  const averageSeconds = useMemo(() => {
    if (!ticketsByForm || ticketsByForm.length === 0) return 0;
    const totalSeconds = ticketsByForm.reduce((sum, ticket) => {
      const seconds = parseDuration(ticket.formFillingDuration);
      return sum + seconds;
    }, 0);
    return totalSeconds / ticketsByForm.length;
  }, [ticketsByForm]);

  function getCountsByDateTime(ticketsByForm) {
    const countsByDateTime = {};

    if (ticketsByForm?.length > 0 && ticketsByForm != null) {
      ticketsByForm.forEach((ticket) => {
        const dateTime = format(parseISO(ticket.createdAt), 'M/d/yyyy, h:mm:ss a');
        countsByDateTime[dateTime] = (countsByDateTime[dateTime] || 0) + 1;
      });
    }
    const chartLabels = Object.keys(countsByDateTime).sort();
    const chartDataPoints = chartLabels.map((dateTime) => countsByDateTime[dateTime]);

    return { chartLabels, chartDataPoints };
  }

  const { chartLabels, chartDataPoints } = useMemo(
    () => getCountsByDateTime(ticketsByForm),
    [ticketsByForm]
  );

  function mapSectionsQuestionsResponses(data) {
    if (!data?.length) return [];

    // Flatten all questions with their section info in one pass
    const allQuestions = data.flatMap(
      (submission) =>
        submission.data?.flatMap(
          (section) =>
            section.questions
              ?.filter((q) => q.inputValue != null)
              ?.map((question) => ({
                sectionId: section.sectionId,
                sectionName: section.sectionName,
                questionId: question.id,
                questionText: question.text,
                questionType: question.type,
                inputValue: question.inputValue,
              })) || []
        ) || []
    );

    // Group by section and question, then process responses
    const groupedData = allQuestions.reduce((acc, item) => {
      const {
        sectionId,
        sectionName,
        questionId,
        questionText,
        questionType,
        inputValue,
      } = item;

      // Initialize section if needed
      if (!acc[sectionId]) {
        acc[sectionId] = {
          sectionId,
          sectionName,
          questions: {},
        };
      }

      // Initialize question if needed
      if (!acc[sectionId].questions[questionId]) {
        acc[sectionId].questions[questionId] = {
          questionId,
          questionText,
          questionType,
          responses: [],
          responseCount: {},
        };
      }

      const questionData = acc[sectionId].questions[questionId];

      // Process responses based on question type
      if (questionType === 'TextInput') {
        const textResponse = Array.isArray(inputValue)
          ? inputValue.join(' ')
          : String(inputValue);
        questionData.responses.push(textResponse);
      } else {
        const values = Array.isArray(inputValue) ? inputValue : [inputValue];
        values.forEach((value) => {
          questionData.responses.push(value);
          questionData.responseCount[value] =
            (questionData.responseCount[value] || 0) + 1;
        });
      }

      return acc;
    }, {});

    // Transform to final format
    return Object.values(groupedData).map((section) => ({
      sectionId: section.sectionId,
      sectionName: section.sectionName,
      questions: Object.values(section.questions),
    }));
  }

  const { deviceCount, OsCount, BrowserCount } = useMemo(() => {
    if (!ticketsByForm || ticketsByForm.length === 0)
      return { deviceCount: 0, OsCount: 0, BrowserCount: 0 };
    const deviceCount = ticketsByForm
      .map((ticket) => ticket.device)
      .reduce((acc, device) => {
        acc[device] = (acc[device] || 0) + 1;
        return acc;
      }, {});

    const OsCount = ticketsByForm
      .map((ticket) => ticket.os)
      .reduce((acc, os) => {
        acc[os] = (acc[os] || 0) + 1;
        return acc;
      }, {});

    const BrowserCount = ticketsByForm
      .map((ticket) => ticket.browser)
      .reduce((acc, browser) => {
        acc[browser] = (acc[browser] || 0) + 1;
        return acc;
      }, {});

    return { deviceCount, OsCount, BrowserCount };
  }, [ticketsByForm]);

  if (!shareableformData || !ticketsByForm) return null;

  if (ticketsByForm.length === 0) return <NoSubmissions />;
  return (
    <Container maxWidth="xl">
      <Backdrop
        sx={{
          bgcolor: 'rgb(121 121 121 / 32%)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <Loader size={isSmallScreen ? 80 : 175} hideText />
        </Box>
      </Backdrop>

      <Box
        sx={{
          display: 'flex',
          flexDirection: isSmallScreen ? 'column' : 'row',
          alignItems: 'center',
          width: '100%',
          mt: '10px',
          mb: '15px',
        }}
      >
        <ActionBar
          title={
            shareableformData
              ? `#${shareableformData.id}_${shareableformData.form.title}`
              : 'shareableform'
          }
          sx={{ marginBottom: '10px' }}
          href={`data:text/csv;charset=utf-8,\uFEFF${encodeURIComponent(csvData)}`}
          downloadName={
            shareableformData
              ? `${shareableformData.id}_${shareableformData.form.title}_survey_results.csv`
              : 'survey_results.csv'
          }
        />
      </Box>

      {/* ------Static Cards_Section--------------- */}

      <Grid container spacing={2} sx={{ marginBottom: 5 }}>
        <Grid item xs={12} md={8}>
          <Box md={12} sx={{ pb: 2 }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="flex-start"
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch' },
                gap: { xs: '10px' },
              }}
            >
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  ml: { xs: 0 },
                }}
              >
                <AppWidgetSummary
                  title={t('dashboard.totalResponses')}
                  total={shareableformData?.submissions ?? 0}
                  icon="raphael:customer"
                  sx={cardStyle}
                  iconStyle={{ mt: '2px', mb: '0px', ml: '3px' }}
                  cardTextStyle={{ flexDirection: 'column-reverse' }}
                  iconSize={6}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  ml: { xs: '0px !important' },
                }}
              >
                <AppWidgetSummary
                  title={t('dashboard.avgCompletionTime')}
                  total={averageSeconds}
                  color="warning"
                  icon="mdi:clock-outline"
                  sx={cardStyle}
                  iconStyle={{ margin: 0 }}
                  cardTextStyle={{ flexDirection: 'column-reverse' }}
                  iconSize={6}
                />
              </Grid>
              <Grid
                item
                sm={12}
                md={4}
                sx={{
                  ml: { xs: '0px !important' },
                }}
              >
                <AppWidgetSummary
                  title={t('dashboard.CompletionRate')}
                  total={
                    ((shareableformData?.submissions ?? 0) /
                      (shareableformData?.visitCount ?? 1)) *
                    100
                  }
                  color="error"
                  icon="material-symbols:percent"
                  sx={cardStyle}
                  iconStyle={{ margin: 0 }}
                  cardTextStyle={{ flexDirection: 'column-reverse' }}
                  iconSize={6}
                />
              </Grid>
            </Stack>
          </Box>

          <Stack direction="row" spacing={2}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  maxWidth: 500,
                  boxShadow:
                    '0 0 12px rgba(145,158,171,0.2), 0 12px 24px -4px rgba(145,158,171,0.12)',
                }}
              >
                <LineChart
                  title={t('dashboard.responseTrends')}
                  subheader=""
                  chartLabels={chartLabels}
                  chartData={[
                    {
                      name: 'Total Responses',
                      type: 'line',
                      fill: 'solid',
                      data: chartDataPoints,
                    },
                  ]}
                  sx={{ height: '100%', maxWidth: 600, boxShadow: '0' }}
                />
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  maxWidth: 600,
                  boxShadow:
                    '0 0 12px rgba(145,158,171,0.2), 0 12px 24px -4px rgba(145,158,171,0.12)',
                }}
              >
                <BarChart
                  height={172}
                  colors={['#7ED348']}
                  isvertical
                  title={t('surveyDashboard.browserUsage')}
                  subheader=""
                  chartData={Object.keys(BrowserCount).map((key) => ({
                    label: key,
                    value: BrowserCount[key],
                  }))}
                />
                <BarChart
                  colors={['#ff9037']}
                  height={172}
                  isvertical
                  title={t('surveyDashboard.operatingsystemusage')}
                  subheader=""
                  chartData={Object.keys(OsCount).map((key) => ({
                    label: getOSName(key) || key,
                    value: OsCount[key],
                  }))}
                />
              </Card>
            </Grid>
          </Stack>
        </Grid>

        <Grid item xs={6} md={4}>
          <Card
            sx={{
              height: '100%',
              maxWidth: 450,
              boxShadow:
                '0 0 12px rgba(145,158,171,0.2), 0 12px 24px -4px rgba(145,158,171,0.12)',
              alignSelf: 'flex-start',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: isSmallScreen && '14px',
                paddingTop: 1,
                paddingLeft: 3,
                marginTop: 2,
              }}
              title={t('dashboard.DeviceDetails')}
            >
              {t('dashboard.DeviceDetails')}
            </Typography>
            <PieChart
              chartData={Object.entries(deviceCount).map(([label, value]) => ({
                label,
                value,
              }))}
              chartColors={[
                theme.palette.primary.main,
                theme.palette.error.main,
                theme.palette.warning.main,
                theme.palette.success.main,
              ]}
              sx={{ height: '100%', maxWidth: 450, boxShadow: '0', mt: 12 }}
            />
          </Card>
        </Grid>
      </Grid>

      {/* ------Questions Analytics_Section------- */}

      <Box
        sx={{
          padding: '10px',
          backgroundColor: '#FFFFFF',
          boxShadow:
            '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: isSmallScreen && '14px',
            pb: 3,
          }}
          title={t('dashboard.questionsanalytics')}
        >
          {t('dashboard.questionsanalytics')}
        </Typography>
        <Box>
          <List
            sx={{
              px: 2,
              backgroundColor: '#F9F8F8',
              maxHeight: '650px',
              overflowY: 'auto',
            }}
          >
            {sections?.map((section, idx) => (
              <AccordionSection
                key={idx}
                title={
                  section.sectionName
                    ? `Section ${section.sectionName}`
                    : `Section ${idx + 1}`
                }
              >
                <Box sx={{ display: 'flex', overflowX: 'auto', gap: 2, py: 2 }}>
                  {section.questions.map((question, qIdx) => (
                    <Card
                      key={qIdx}
                      sx={{
                        minWidth: 250,
                        maxWidth: 400,
                        flexShrink: 0,
                        px: 2,
                        py: 1,
                        boxShadow:
                          '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
                      }}
                    >
                      {(question.questionType === 'TextInput' ||
                        question.questionType === 'TextAreaInput') && (
                        <TextInputCard question={question} />
                      )}

                      {question.questionType === 'DateTimePickerInput' && (
                        <DateInpuCard question={question} />
                      )}
                      {question.questionType === 'FileInput' && (
                        <FileInputCard question={question} />
                      )}
                      {question.questionType !== 'TextInput' &&
                        question.questionType !== 'DateTimePickerInput' &&
                        question.questionType !== 'TextAreaInput' &&
                        question.questionType !== 'FileInput' && (
                          <MultipleInputCard question={question} />
                        )}
                    </Card>
                  ))}
                </Box>
              </AccordionSection>
            ))}
          </List>
        </Box>
      </Box>
    </Container>
  );
}
